'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';

interface AiPromptBoxProps {
  promptText: string;
}

export default function AiPromptBox({ promptText }: AiPromptBoxProps) {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = () => {
    if (typeof navigator !== 'undefined') {
      navigator.clipboard.writeText(promptText).then(() => {
        setCopied(true);
        toast.success('Prompt copied to clipboard!');
        setTimeout(() => setCopied(false), 2500);
      });
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(14, 203, 129, 0.05) 0%, rgba(30, 35, 41, 0.95) 100%)',
      border: '1px solid rgba(14, 203, 129, 0.2)',
      borderRadius: 'var(--radius-lg)',
      padding: '24px',
      boxShadow: 'var(--shadow-card)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: 700,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--primary)' }}>
          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path>
          <path d="M20 3v4"></path>
          <path d="M22 5h-4"></path>
          <path d="M4 17v2"></path>
          <path d="M5 18H3"></path>
        </svg>
        Want your own version?
      </h2>

      <p style={{ fontSize: '14px', color: 'var(--muted-strong)', marginTop: '6px' }}>
        Start from this prompt and change whatever you like — the armor, the weapon, the palette.
      </p>

      <pre style={{
        background: '#14181d',
        border: '1px solid var(--hairline-border)',
        borderRadius: 'var(--radius-md)',
        padding: '14px 16px',
        fontFamily: 'var(--font-mono)',
        fontSize: '13px',
        lineHeight: 1.6,
        color: '#c8d0da',
        marginTop: '16px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {promptText}
      </pre>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '18px' }}>
        <Button
          onClick={handleCopy}
          type="button"
          variant="secondary"
          className="gap-2 border-border font-semibold"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
          </svg>
          {copied ? '✓ Copied!' : 'Copy prompt'}
        </Button>

        <Button asChild variant="link" className="text-primary hover:text-primary/80 gap-1.5 p-0 h-auto font-semibold">
          <a
            href="/"
            onClick={() => {
              if (typeof window !== 'undefined') {
                sessionStorage.setItem('openAiStudio', 'true');
              }
            }}
          >
            Open the generator
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14"></path>
              <path d="m12 5 7 7-7 7"></path>
            </svg>
          </a>
        </Button>
      </div>
    </div>
  );
}
